/**
 * Format bathroom number to show as integer if it's a whole number, 
 * otherwise show as decimal (e.g., 2.0 -> 2, 2.5 -> 2.5)
 * @param {number|string} bathrooms - The bathroom count
 * @returns {string|number} - Formatted bathroom count
 */
export const formatBathrooms = (bathrooms) => {
  if (bathrooms === null || bathrooms === undefined || bathrooms === '') {
    return '-';
  }
  
  const num = Number(bathrooms);
  if (isNaN(num)) {
    return bathrooms;
  }
  
  return num % 1 === 0 ? parseInt(num) : num;
};

/**
 * True when both bedroom and bathroom counts are present (0 bedrooms = studio is valid).
 */
export const hasBedroomAndBathroomValues = (bedrooms, bathrooms) => {
  if (bedrooms === null || bedrooms === undefined || bedrooms === '') return false;
  if (bathrooms === null || bathrooms === undefined || bathrooms === '') return false;
  return !Number.isNaN(Number(bedrooms)) && !Number.isNaN(Number(bathrooms));
};

/**
 * Single bedroom count for display (0 → Studio).
 */
export const formatBedroomsLabel = (bedrooms) => {
  if (bedrooms === null || bedrooms === undefined || bedrooms === '') {
    return '-';
  }
  const n = Number(bedrooms);
  if (Number.isNaN(n)) {
    return String(bedrooms);
  }
  if (n === 0) {
    return 'Studio';
  }
  return n % 1 === 0 ? parseInt(n, 10) : n;
};

/**
 * Format bedroom and bathroom combination (e.g., "2B2.5B"; studio → "Studio · 1B")
 * @param {number|string} bedrooms - The bedroom count
 * @param {number|string} bathrooms - The bathroom count
 * @returns {string} - Formatted combination
 */
export const formatBedroomBathroom = (bedrooms, bathrooms) => {
  if (!hasBedroomAndBathroomValues(bedrooms, bathrooms)) {
    return '-';
  }

  const bedNum = Number(bedrooms);
  const bathNum = Number(bathrooms);
  if (Number.isNaN(bedNum) || Number.isNaN(bathNum)) {
    return '-';
  }

  const formattedBathrooms = formatBathrooms(bathrooms);
  if (bedNum === 0) {
    return `Studio · ${formattedBathrooms}B`;
  }
  return `${bedNum}B${formattedBathrooms}B`;
};

/**
 * 格式化可用日期
 * 如果是当天或之前的日期显示 "Now"，否则显示完整日期
 * @param {string|Date} availableDate - 可用日期
 * @returns {string} 格式化后的日期字符串
 */
export const formatAvailabilityDate = (availableDate) => {
  if (!availableDate || availableDate === 'now') {
    return 'Now';
  }
  
  try {
    const date = new Date(availableDate);
    const today = new Date();
    
    // 重置时间为00:00:00进行比较
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // 如果是当天或之前的日期，显示 "Now"
    if (dateOnly <= todayOnly) {
      return 'Now';
    }
    
    // 否则显示月/日/年格式
    return date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  } catch (error) {
    // 如果日期解析失败，返回原始值
    return availableDate;
  }
}; 